import { CATEGO_CPT_MAP, SENS_SOLDE_MAP, TYP_CPT_MAP } from './pcemfCorrespondanceMaps';

// Correspondance compte / poste (source : "Table de correspondance compte et poste")
// Détermine les valeurs par défaut TYPCPT, TYPCLI, categocpt et sensSolde
// en fonction du numéro de compte PCEMF (recherche par préfixe le plus long).

const longestPrefixLookup = (map: Record<string, string>, code: string): string | undefined => {
  for (let i = code.length; i > 0; i--) {
    const val = map[code.substring(0, i)];
    if (val !== undefined) return val;
  }
  return undefined;
};

export interface PcemfDefaults {
  TYPCPT: string;
  TYPCLI: string;
  categocpt: string;
  sensSolde: string;
}

// Configuration par défaut complète : les tables de correspondance (par préfixe
// de compte) + les valeurs de repli utilisées quand aucun préfixe ne correspond.
export interface PcemfDefaultsConfig {
  sensSolde: Record<string, string>;
  categocpt: Record<string, string>;
  typcpt: Record<string, string>;
  fallback: PcemfDefaults;
}

const BUILTIN_CONFIG: PcemfDefaultsConfig = {
  sensSolde: SENS_SOLDE_MAP,
  categocpt: CATEGO_CPT_MAP,
  typcpt: TYP_CPT_MAP,
  fallback: { TYPCPT: 'I', TYPCLI: 'I', categocpt: 'I', sensSolde: 'I' },
};

// Config active : celle intégrée par défaut, remplaçable via importPcemfConfig().
let activeConfig: PcemfDefaultsConfig = BUILTIN_CONFIG;

export const getPcemfConfig = (): PcemfDefaultsConfig => activeConfig;

export const resetPcemfConfig = () => { activeConfig = BUILTIN_CONFIG; };

// Valide et remplace la config active à partir d'un JSON importé.
// Lève une erreur si la structure est invalide.
export const importPcemfConfig = (json: string): PcemfDefaultsConfig => {
  const parsed = JSON.parse(json);
  const isMap = (v: any) => v && typeof v === 'object' && !Array.isArray(v) &&
    Object.values(v).every(x => typeof x === 'string');
  if (!isMap(parsed?.sensSolde) || !isMap(parsed?.categocpt) || !isMap(parsed?.typcpt)) {
    throw new Error("Format invalide : les clés 'sensSolde', 'categocpt' et 'typcpt' doivent être des objets { préfixe: code }.");
  }
  const fb = parsed.fallback ?? {};
  activeConfig = {
    sensSolde: parsed.sensSolde,
    categocpt: parsed.categocpt,
    typcpt: parsed.typcpt,
    fallback: {
      TYPCPT: typeof fb.TYPCPT === 'string' ? fb.TYPCPT : 'I',
      TYPCLI: typeof fb.TYPCLI === 'string' ? fb.TYPCLI : 'I',
      categocpt: typeof fb.categocpt === 'string' ? fb.categocpt : 'I',
      sensSolde: typeof fb.sensSolde === 'string' ? fb.sensSolde : 'I',
    },
  };
  return activeConfig;
};

// Sérialise la config active (pour export/téléchargement).
export const exportPcemfConfig = (): string => JSON.stringify(activeConfig, null, 2);

// --- Conversion Excel (format tabulaire à plat) --------------------------
// Une ligne par entrée : { Champ, Prefixe, Code }
//  - Champ = 'sensSolde' | 'categocpt' | 'typcpt' | 'fallback'
//  - Prefixe = préfixe de compte (ou nom du champ pour 'fallback')
//  - Code = valeur à appliquer
export interface PcemfConfigRow {
  Champ: string;
  Prefixe: string;
  Code: string;
}

export const pcemfConfigToRows = (cfg: PcemfDefaultsConfig = activeConfig): PcemfConfigRow[] => {
  const rows: PcemfConfigRow[] = [];
  (['sensSolde', 'categocpt', 'typcpt'] as const).forEach(champ => {
    Object.entries(cfg[champ]).forEach(([prefixe, code]) =>
      rows.push({ Champ: champ, Prefixe: prefixe, Code: code }));
  });
  (Object.entries(cfg.fallback) as [string, string][]).forEach(([champ, code]) =>
    rows.push({ Champ: 'fallback', Prefixe: champ, Code: code }));
  return rows;
};

// Reconstruit et active une config depuis des lignes Excel importées.
export const importPcemfConfigFromRows = (rows: any[]): PcemfDefaultsConfig => {
  const cfg: PcemfDefaultsConfig = {
    sensSolde: {},
    categocpt: {},
    typcpt: {},
    fallback: { TYPCPT: 'I', TYPCLI: 'I', categocpt: 'I', sensSolde: 'I' },
  };
  let count = 0;
  rows.forEach(row => {
    const champ = String(row.Champ ?? row.champ ?? '').trim();
    const prefixe = String(row.Prefixe ?? row.prefixe ?? row.Préfixe ?? '').trim();
    const code = String(row.Code ?? row.code ?? '').trim();
    if (!champ || !prefixe || !code) return;
    if (champ === 'sensSolde' || champ === 'categocpt' || champ === 'typcpt') {
      cfg[champ][prefixe] = code;
      count++;
    } else if (champ === 'fallback' && prefixe in cfg.fallback) {
      (cfg.fallback as any)[prefixe] = code;
      count++;
    }
  });
  if (count === 0) {
    throw new Error("Aucune ligne valide trouvée. Colonnes attendues : 'Champ' (sensSolde/categocpt/typcpt/fallback), 'Prefixe', 'Code'.");
  }
  activeConfig = cfg;
  return activeConfig;
};

export const getPcemfDefaults = (idPlan: string | number | undefined): PcemfDefaults => {
  const code = String(idPlan ?? '').trim();
  const cfg = activeConfig;
  return {
    TYPCPT: longestPrefixLookup(cfg.typcpt, code) ?? cfg.fallback.TYPCPT,
    TYPCLI: cfg.fallback.TYPCLI,
    categocpt: longestPrefixLookup(cfg.categocpt, code) ?? cfg.fallback.categocpt,
    sensSolde: longestPrefixLookup(cfg.sensSolde, code) ?? cfg.fallback.sensSolde,
  };
};
